import { Roles } from '@/entities/roles'
import { TopupsModel, TopupsModelMerchant, TopupStatus, TopupTraderStatus } from '@/entities/topups'
import { BaseButton } from '@/shared/components'
import { ItemLayout } from '@/shared/components/ItemLayout/ItemLayout'
import { DealIcon } from '@/shared/components/icons/DealIcon'
import { formatCurrency } from '@/shared/lib/currency'
import { formatDate } from '@/shared/lib/date'
import { getShortenId } from '@/shared/utils/getShortenId'
import { ColumnDef } from '@tanstack/react-table'
import { TopupStatusIcons, TopupStatusTitles } from '../model/constans'
import { Link } from 'react-router-dom'
import { CopyBlock } from '@/shared/components/CopyBlock/CopyBlock'
import { formatRequisite } from '@/shared/lib'

interface Props {
  onConfirm?: (id: string) => void
  userRole: Roles | null
  selectedStatus?: number
}

export const getColumns = ({
  onConfirm,
  userRole,
  selectedStatus,
}: Props): ColumnDef<TopupsModel | TopupsModelMerchant>[] => {
  const isMerchant = userRole === Roles.Merchant
  const isTrader = userRole === Roles.Trader || userRole === Roles.Admin

  const showExchangeColumn = () => {
    return (
      isMerchant ||
      selectedStatus === TopupTraderStatus.All ||
      selectedStatus === TopupTraderStatus.Paid
    )
  }

  const baseColumns: ColumnDef<TopupsModel | TopupsModelMerchant>[] = [
    {
      accessorKey: 'id',
      header: () => <div className="uppercase">id</div>,
      cell: ({ row }) => {
        const id = row.getValue<string>('id')

        return (
          <div className="flex flex-col">
            <CopyBlock text={id} title={id}>
              <Link to={id} className="text-primary">
                {getShortenId(id)}
              </Link>
            </CopyBlock>
            {userRole === Roles.Merchant && (
              <>
                {(row.original as TopupsModelMerchant).payerId && (
                  <CopyBlock
                    text={(row.original as TopupsModelMerchant).payerId}
                    title={(row.original as TopupsModelMerchant).payerId}
                    color="secondary"
                    className="text-xs"
                  >
                    payer_id: {getShortenId((row.original as TopupsModelMerchant).payerId)}
                  </CopyBlock>
                )}

                {(row.original as TopupsModelMerchant).merchantTopupId && (
                  <CopyBlock
                    text={(row.original as TopupsModelMerchant).merchantTopupId}
                    title={(row.original as TopupsModelMerchant).merchantTopupId}
                    color="secondary"
                    className="text-xs"
                  >
                    merchant_topup_id:{' '}
                    {getShortenId((row.original as TopupsModelMerchant).merchantTopupId)}
                  </CopyBlock>
                )}
              </>
            )}
          </div>
        )
      },
    },
    {
      accessorKey: 'deal',
      header: 'Сделка',
      cell: ({ row }) => {
        const createdAt = row.original.createdAt || ''
        const status = row.original.status
        const title = TopupStatusTitles[status]
        const iconStatus = TopupStatusIcons[status]
        const id = row.original.id
        return (
          <Link to={id}>
            <ItemLayout
              icon={<DealIcon status={iconStatus} />}
              subtitle={new Date(createdAt).toLocaleString('ru-RU')}
              title={title}
            />
          </Link>
        )
      },
    },
    {
      accessorKey: 'amount',
      header: 'Сумма',
      cell: ({ row }) => {
        const amountFrom =
          row.original.factAmountFrom && row.original.factAmountFrom !== 0
            ? row.original.factAmountFrom
            : row.original.amountFrom
        const amountTo =
          row.original.factAmountTo && row.original.factAmountTo !== 0
            ? row.original.factAmountTo
            : row.original.amountTo

        return (
          <div className="flex flex-col">
            <CopyBlock text={amountTo?.toFixed(2)}>
              {formatCurrency(amountTo ?? 0, row.original.currencyTo)}
            </CopyBlock>

            <CopyBlock text={amountFrom?.toFixed(2)} color="secondary">
              {formatCurrency(amountFrom ?? 0, row.original.currencyFrom)}
            </CopyBlock>
          </div>
        )
      },
    },
    {
      accessorKey: 'recipient',
      header: 'Получатель',
      cell: ({ row }) => {
        const requisite = formatRequisite(
          row.original.paymentRequisitToValue
        )
        return (
          <div className="flex flex-col">
            <CopyBlock text={row.original.paymentRequisitToValue}>{requisite}</CopyBlock>
          </div>
        )
      },
    },
  ]

  if (showExchangeColumn()) {
    baseColumns.push({
      accessorKey: 'exchange',
      header: 'Курс',
      cell: ({ row }) => {
        const rate = row.original.exchangeRate
        const time = formatDate(row.original.createdAt, 'timeOnly')

        const showRate = [
          TopupStatus.Paid,
          TopupStatus.OverPaid,
          TopupStatus.PartiallyPaid,
        ].includes(row.original.status)

        if (showRate) {
          return (
            <div className="flex flex-col">
              <span>{rate ? `${rate} RUB` : ''}</span>
              <span className="text-text-secondary">{rate ? `курс на ${time}` : ''}</span>
            </div>
          )
        }
        return null
      },
    })
  }

  const merchantOnlyColumn: ColumnDef<TopupsModelMerchant> = {
    accessorKey: 'commissionedAmount',
    header: 'Начислено',
    cell: ({ row }) => {
      const isMerchantRow = 'commission' in row.original
      if (!isMerchantRow) return null

      const merchantRow = row.original
      const amount = row.original.status === TopupStatus.Paid ? merchantRow.amountTo : 0
      return <span>{amount !== 0 ? formatCurrency(amount, merchantRow.currencyTo) : ''}</span>
    },
  }

  const traderOnlyColumn: ColumnDef<TopupsModel> = {
    accessorKey: 'action',
    header: '',
    cell: ({ row }) => {
      const isTraderRow = !('commission' in row.original)
      if (!isTraderRow) return null

      const status = row.original.status
      if (status === TopupStatus.WaitingPayment) {
        return (
          <BaseButton
            radius="lg"
            className="cursor-pointer"
            onClick={(e: { stopPropagation: () => void }) => {
              e.stopPropagation()
              onConfirm?.(row.original.id)
            }}
          >
            Подтвердить
          </BaseButton>
        )
      }
      return null
    },
  }

  return [
    ...baseColumns,
    ...(isMerchant ? [merchantOnlyColumn as ColumnDef<TopupsModel | TopupsModelMerchant>] : []),
    ...(isTrader ? [traderOnlyColumn as ColumnDef<TopupsModel | TopupsModelMerchant>] : []),
  ]
}
